import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const FIELD_TYPES = [
  { key: 'region', label: 'Bölge', icon: 'bx-map' },
  { key: 'department', label: 'Departman', icon: 'bx-sitemap' }
];

const emptyFields = () => FIELD_TYPES.reduce((result, item) => ({ ...result, [item.key]: [] }), {});

function OrganizationFieldsPage() {
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [fields, setFields] = useState(emptyFields);
  const [newValues, setNewValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadFields = useCallback(async (selectedCompanyId) => {
    try {
      setLoading(true);
      const response = await api.getOrganizationFields(true, selectedCompanyId);
      setCompanies(response.companies || []);
      setFields(response.data || emptyFields());
      setCompanyId((current) => selectedCompanyId || current || response.companies?.[0]?._id || '');
    } catch (error) {
      showError(error.message || 'Organizasyon alanları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { loadFields(''); }, [loadFields]);

  const addField = async (type) => {
    const name = String(newValues[type] || '').trim();
    if (!companyId) return showError('Önce şirket seçin.');
    if (!name) return showError('Eklenecek değeri yazın.');
    try {
      setSaving(true);
      await api.createOrganizationField({ type, name, companyId });
      setNewValues((current) => ({ ...current, [type]: '' }));
      showSuccess('Değer eklendi.');
      await loadFields(companyId);
    } catch (error) {
      showError(error.message || 'Değer eklenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const renameField = async (field) => {
    const name = window.prompt('Yeni değer', field.name)?.trim();
    if (!name || name === field.name) return;
    try {
      await api.updateOrganizationField(field._id, { name });
      showSuccess('Değer güncellendi.');
      await loadFields(companyId);
    } catch (error) {
      showError(error.message || 'Değer güncellenemedi.');
    }
  };

  const toggleField = async (field) => {
    try {
      if (field.isActive) await api.deleteOrganizationField(field._id);
      else await api.updateOrganizationField(field._id, { isActive: true });
      showSuccess(field.isActive ? 'Değer pasifleştirildi.' : 'Değer aktifleştirildi.');
      await loadFields(companyId);
    } catch (error) {
      showError(error.message || 'Durum değiştirilemedi.');
    }
  };

  return (
    <div className="documents-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="modern-card p-4 mb-4">
        <h3 className="fw-bold text-dark mb-1">Organizasyon Alanları</h3>
        <p className="text-muted mb-3">Bölge ve departmanlar seçilen şirkete özeldir. Pozisyon serbest metin olarak kullanıcı kaydında girilir.</p>
        <label className="modern-form-label">Şirket</label>
        <select className="form-select modern-input" style={{ maxWidth: 360 }} value={companyId} onChange={(event) => { setCompanyId(event.target.value); loadFields(event.target.value); }}>
          <option value="">Şirket seçin</option>
          {companies.map((company) => <option key={company._id} value={company._id}>{company.name}</option>)}
        </select>
      </div>
      {loading ? <div className="modern-loading"><div className="spinner-border" /></div> : (
        <div className="row g-4">
          {FIELD_TYPES.map((type) => (
            <div className="col-xl-6" key={type.key}>
              <section className="modern-card p-4 h-100">
                <h5 className="fw-bold mb-3"><i className={`bx ${type.icon} me-2`} />{type.label}</h5>
                <div className="d-flex gap-2 mb-3">
                  <input
                    className="form-control modern-input"
                    value={newValues[type.key] || ''}
                    placeholder={`Yeni ${type.label.toLocaleLowerCase('tr-TR')}`}
                    onChange={(event) => setNewValues((current) => ({ ...current, [type.key]: event.target.value }))}
                    onKeyDown={(event) => event.key === 'Enter' && addField(type.key)}
                  />
                  <button className="btn btn-modern-primary" disabled={saving || !companyId} onClick={() => addField(type.key)}>Ekle</button>
                </div>
                <div className="list-group list-group-flush">
                  {(fields[type.key] || []).map((field) => (
                    <div key={field._id} className="list-group-item px-0 d-flex align-items-center justify-content-between gap-2">
                      <span className={field.isActive ? '' : 'text-muted text-decoration-line-through'}>{field.name}</span>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => renameField(field)}>Düzenle</button>
                        <button className={`btn btn-sm ${field.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={() => toggleField(field)}>
                          {field.isActive ? 'Pasifleştir' : 'Aktifleştir'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {!fields[type.key]?.length && <p className="text-muted mb-0">Bu şirket için henüz değer tanımlanmadı.</p>}
                </div>
              </section>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrganizationFieldsPage;
